import React, { useCallback, useRef, useEffect } from 'react'
import { GoogleMap, useJsApiLoader } from '@react-google-maps/api'
import { MapPin, Loader2, X } from 'lucide-react'

const MAP_CONTAINER_STYLE = { width: '100%', height: '280px', borderRadius: '12px' }

export interface MapCoordinates {
  lat: number
  lng: number
}

interface MapDisplayProps {
  coordinates: MapCoordinates
  onClose?: () => void
}

/** Read-only map. Only loads Google Maps when mounted - use with conditional render to save API credits. */
const MapDisplay: React.FC<MapDisplayProps> = ({ coordinates, onClose }) => {
  const markerRef = useRef<google.maps.Marker | null>(null)
  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string | undefined

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: apiKey || '',
  })

  const onMapLoad = useCallback(
    (map: google.maps.Map) => {
      const pos = { lat: Number(coordinates.lat), lng: Number(coordinates.lng) }
      const marker = new google.maps.Marker({
        position: pos,
        map,
        title: 'School location',
        optimized: false,
        animation: google.maps.Animation.DROP,
        zIndex: 999,
      })
      markerRef.current = marker
    },
    [coordinates.lat, coordinates.lng]
  )

  useEffect(() => {
    return () => {
      if (markerRef.current) {
        markerRef.current.setMap(null)
        markerRef.current = null
      }
    }
  }, [])

  if (!apiKey || apiKey.trim() === '') {
    return (
      <div className="rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 p-6 text-center">
        <MapPin className="h-10 w-10 text-gray-400 mx-auto mb-2" />
        <p className="text-sm text-gray-600">Map not configured. Coordinates: {coordinates.lat.toFixed(5)}, {coordinates.lng.toFixed(5)}</p>
        {onClose && (
          <button type="button" onClick={onClose} className="mt-3 text-sm text-primary-600 hover:text-primary-700">
            Close
          </button>
        )}
      </div>
    )
  }

  if (loadError) {
    return (
      <div className="rounded-xl border-2 border-dashed border-red-200 bg-red-50 p-6 text-center">
        <p className="text-sm text-red-700">Failed to load map.</p>
        {onClose && (
          <button type="button" onClick={onClose} className="mt-3 text-sm text-primary-600 hover:text-primary-700">
            Close
          </button>
        )}
      </div>
    )
  }

  if (!isLoaded) {
    return (
      <div className="rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 p-12 text-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600 mx-auto mb-2" />
        <p className="text-sm text-gray-600">Loading map...</p>
      </div>
    )
  }

  return (
    <div className="rounded-xl overflow-hidden border border-gray-200 shadow-sm relative">
      {onClose && (
        <button
          type="button"
          onClick={onClose}
          className="absolute top-2 right-2 z-10 p-1.5 bg-white rounded-lg shadow border border-gray-200 hover:bg-gray-50"
          aria-label="Close map"
        >
          <X className="h-4 w-4 text-gray-600" />
        </button>
      )}
      <GoogleMap
        mapContainerStyle={MAP_CONTAINER_STYLE}
        center={{ lat: Number(coordinates.lat), lng: Number(coordinates.lng) }}
        zoom={15}
        onLoad={onMapLoad}
        options={{
          scrollwheel: true,
          disableDefaultUI: false,
          zoomControl: true,
          mapTypeControl: true,
          streetViewControl: false,
          fullscreenControl: true,
        }}
      />
    </div>
  )
}

export default MapDisplay
